import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './supabaseClient';

const bibleVerses = [
  { verse: "I can do all things through Christ who strengthens me.", ref: "Philippians 4:13" },
  { verse: "Trust in the Lord with all your heart.", ref: "Proverbs 3:5" },
  { verse: "For I know the plans I have for you, declares the Lord.", ref: "Jeremiah 29:11" },
  { verse: "The Lord is my shepherd; I shall not want.", ref: "Psalm 23:1" },
  { verse: "Be strong and courageous.", ref: "Joshua 1:9" },
  { verse: "Cast all your anxiety on him because he cares for you.", ref: "1 Peter 5:7" },
];

const getDailyVerse = () => bibleVerses[Math.floor((Date.now() / 86400000) % bibleVerses.length)];
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const defaultWorkouts = Object.fromEntries(DAYS.map(d => [d, {}]));
const defaultTracking = Object.fromEntries(DAYS.map(d => [d, {}]));

const getWeekStart = (offset = 0) => {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1) + (offset * 7);
  return new Date(now.setDate(diff)).toISOString().split('T')[0];
};

const formatWeek = (d) => {
  const start = new Date(d + 'T00:00:00');
  const end = new Date(start); end.setDate(end.getDate() + 6);
  return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
};

const getCurrentDay = () => ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][new Date().getDay()];

const formatDue = (d) => {
  if (!d) return null;
  const date = new Date(d + 'T00:00:00');
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tmrw = new Date(today); tmrw.setDate(tmrw.getDate() + 1);
  if (date.getTime() === today.getTime()) return { label: 'Today', color: '#f59e0b' };
  if (date.getTime() === tmrw.getTime()) return { label: 'Tomorrow', color: '#3b82f6' };
  if (date < today) return { label: 'Overdue', color: '#ef4444' };
  return { label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), color: '#64748b' };
};

const TANNER_STATUSES = [
  { id: 'todo', label: 'To Do', color: '#64748b', bg: '#f1f5f9' },
  { id: 'inprogress', label: 'In Progress', color: '#3b82f6', bg: '#eff6ff' },
  { id: 'done', label: 'Done', color: '#10b981', bg: '#f0fdf4' },
];

const ELI_STATUSES = [
  { id: 'todo', label: 'To Do', color: '#64748b', bg: '#f1f5f9' },
  { id: 'inprogress', label: 'In Progress', color: '#3b82f6', bg: '#eff6ff' },
  { id: 'review', label: 'Review', color: '#f59e0b', bg: '#fffbeb' },
  { id: 'needs_context', label: 'Needs Context', color: '#8b5cf6', bg: '#f5f3ff' },
  { id: 'invalid', label: 'Invalid', color: '#94a3b8', bg: '#f8fafc' },
  { id: 'done', label: 'Done', color: '#10b981', bg: '#f0fdf4' },
];

const PRIORITIES = [
  { id: 'high', label: 'High', color: '#ef4444', border: '#ef4444' },
  { id: 'medium', label: 'Medium', color: '#f59e0b', border: '#f59e0b' },
  { id: 'normal', label: 'Normal', color: '#64748b', border: '#e2e8f0' },
];

const getStatus = (statusId, statusArray) => statusArray.find(s => s.id === statusId) || statusArray[0];

const priorityOrder = { high: 0, medium: 1, normal: 2 };
const statusOrder = { todo: 0, inprogress: 1, review: 2, done: 3 };

const sortTasks = (tasks, sortBy) => {
  return [...tasks].sort((a, b) => {
    if (sortBy === 'priority') {
      const pa = priorityOrder[a.priority || 'normal'];
      const pb = priorityOrder[b.priority || 'normal'];
      if (pa !== pb) return pa - pb;
    }
    if (sortBy === 'due') {
      const da = a.due_date || '9999-12-31';
      const db = b.due_date || '9999-12-31';
      if (da !== db) return da < db ? -1 : 1;
    }
    if (sortBy === 'status') {
      const sa = statusOrder[a.status || 'todo'];
      const sb = statusOrder[b.status || 'todo'];
      if (sa !== sb) return sa - sb;
    }
    return 0;
  });
};

const cats = {
  personal: { label: 'Personal', color: '#10b981', bg: 'rgba(16,185,129,0.1)', icon: '○' },
  family: { label: 'Family', color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', icon: '♡' },
  spiritual: { label: 'Spiritual', color: '#a855f7', bg: 'rgba(168,85,247,0.1)', icon: '✝' },
  premium: { label: 'Premium', color: '#3b82f6', bg: 'rgba(59,130,246,0.1)', icon: '◈' },
  dripjobs: { label: 'DripJobs', color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)', icon: '◆' },
  routemize: { label: 'Routemize', color: '#ec4899', bg: 'rgba(236,72,153,0.1)', icon: '◇' },
};

function WeeklyTracker({ session }) {
  const [goals, setGoals] = useState(Object.fromEntries(Object.keys(cats).map(c => [c, []])));
  const [workouts, setWorkouts] = useState(defaultWorkouts);
  const [tasks, setTasks] = useState([]);
  const [dailyTracking, setDailyTracking] = useState(defaultTracking);
  const [editingGoal, setEditingGoal] = useState(null);
  const [newGoalText, setNewGoalText] = useState('');
  const [newGoalDesc, setNewGoalDesc] = useState('');
  const [newGoalDue, setNewGoalDue] = useState('');
  const [newGoalDaily, setNewGoalDaily] = useState(false);
  const [newGoalRecurring, setNewGoalRecurring] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(0);
  const [weekStart, setWeekStart] = useState(getWeekStart(0));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [eliTasks, setEliTasks] = useState([]);
  const [tannerTasks, setTannerTasks] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [editText, setEditText] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editDaily, setEditDaily] = useState(false);
  const [editRecurring, setEditRecurring] = useState(false);
  const [editDue, setEditDue] = useState('');
  const [copiedLink, setCopiedLink] = useState(null);
  const [hideDone, setHideDone] = useState(() => {
    try { const v = localStorage.getItem('hideDone'); return v === null ? true : JSON.parse(v); } catch { return true; }
  });
  const [eliSort, setEliSort] = useState('priority');
  const [tannerSort, setTannerSort] = useState('priority');
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskDue, setNewTaskDue] = useState('');
  const [newTaskPriority, setNewTaskPriority] = useState('normal');
  const [newTaskNotes, setNewTaskNotes] = useState('');
  const [addingTask, setAddingTask] = useState(false);
  const [openStatusDropdown, setOpenStatusDropdown] = useState(null);
  const [contextModal, setContextModal] = useState(null); // { id, taskName }
  const [contextComment, setContextComment] = useState('');
  const [expandedTasks, setExpandedTasks] = useState({});
  const [goalsCollapsed, setGoalsCollapsed] = useState(true);
  const [workoutsCollapsed, setWorkoutsCollapsed] = useState(true);
  const [editTannerTask, setEditTannerTask] = useState(null);
  const [editTannerName, setEditTannerName] = useState('');
  const [editTannerDue, setEditTannerDue] = useState('');
  const [editTannerNotes, setEditTannerNotes] = useState('');
  const [editTannerPriority, setEditTannerPriority] = useState('normal');
  const [editTannerStatus, setEditTannerStatus] = useState('todo');
  const [editTannerSaving, setEditTannerSaving] = useState(false);
  const scrollPos = useRef(0);

  useEffect(() => {
    try { localStorage.setItem('hideDone', JSON.stringify(hideDone)); } catch {}
  }, [hideDone]);

  const userId = session?.user?.id;
  const currentDay = getCurrentDay();
  const verse = getDailyVerse();

  useEffect(() => { setWeekStart(getWeekStart(selectedWeek)); }, [selectedWeek]);

  const saveScroll = () => { scrollPos.current = window.scrollY; };
  const restoreScroll = () => { setTimeout(() => window.scrollTo(0, scrollPos.current), 0); };

  const loadEli = useCallback(async () => {
    const { data } = await supabase.from('eli_tasks').select('*').order('created_at', { ascending: false });
    setEliTasks(data || []);
  }, []);

  const loadTanner = useCallback(async () => {
    const { data } = await supabase.from('tanner_tasks').select('*').order('created_at', { ascending: false });
    setTannerTasks(data || []);
  }, []);

  useEffect(() => { loadEli(); loadTanner(); }, [loadEli, loadTanner]);

  const loadData = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    const { data } = await supabase.from('weekly_tracker').select('*').eq('user_id', userId).eq('week_start', weekStart).single();
    if (data) {
      setGoals(data.goals || Object.fromEntries(Object.keys(cats).map(c => [c, []])));
      setWorkouts(data.workouts || defaultWorkouts);
      setTasks(data.tasks || []);
      setDailyTracking({ ...defaultTracking, ...(data.daily_tracking || {}) });
    } else {
      let recurring = Object.fromEntries(Object.keys(cats).map(c => [c, []]));
      const { data: all } = await supabase.from('weekly_tracker').select('*').eq('user_id', userId).neq('week_start', weekStart);
      if (all) all.forEach(w => {
        if (w.goals) Object.keys(recurring).forEach(c => {
          (w.goals[c] || []).filter(g => g.isRecurring).forEach(g => {
            if (!recurring[c].find(e => e.text === g.text)) recurring[c].push({ ...g, id: Date.now() + Math.random(), completed: false });
          });
        });
      });
      setGoals(recurring);
      setWorkouts(defaultWorkouts);
      setTasks([]);
      setDailyTracking(defaultTracking);
    }
    setLoading(false);
  }, [userId, weekStart]);

  useEffect(() => { loadData(); }, [loadData]);

  const save = async (g, w, t, d) => {
    if (!userId) return;
    setSaving(true);
    await supabase.from('weekly_tracker').upsert({ user_id: userId, week_start: weekStart, goals: g || goals, workouts: w || workouts, tasks: t || tasks, daily_tracking: d || dailyTracking }, { onConflict: 'user_id,week_start' });
    setSaving(false);
  };

  const toggleGoal = (cat, id) => { saveScroll(); const g = { ...goals, [cat]: goals[cat].map(x => x.id === id ? { ...x, completed: !x.completed } : x) }; setGoals(g); save(g); restoreScroll(); };
  const toggleDaily = (cat, id, day) => { saveScroll(); const d = { ...dailyTracking, [day]: { ...dailyTracking[day], [`${cat}-${id}`]: !dailyTracking[day]?.[`${cat}-${id}`] } }; setDailyTracking(d); save(null, null, null, d); restoreScroll(); };
  const addGoal = (cat) => { if (!newGoalText.trim()) return; const g = { ...goals, [cat]: [...goals[cat], { id: Date.now(), text: newGoalText.trim(), description: newGoalDesc.trim() || null, completed: false, isDaily: newGoalDaily, isRecurring: newGoalRecurring, dueDate: newGoalDue || null }] }; setGoals(g); save(g); setNewGoalText(''); setNewGoalDesc(''); setEditingGoal(null); setNewGoalDaily(false); setNewGoalRecurring(false); setNewGoalDue(''); };
  const removeGoal = (cat, id) => { const g = { ...goals, [cat]: goals[cat].filter(x => x.id !== id) }; setGoals(g); save(g); };
  const updateWorkout = (day, field, val) => { saveScroll(); const w = { ...workouts, [day]: { ...workouts[day], [field]: val } }; setWorkouts(w); save(null, w); restoreScroll(); };

  const deleteEli = async (id) => { await supabase.from('eli_tasks').delete().eq('id', id); setEliTasks(eliTasks.filter(x => x.id !== id)); };
  const deleteTanner = async (id) => { await supabase.from('tanner_tasks').delete().eq('id', id); setTannerTasks(tannerTasks.filter(x => x.id !== id)); };

  const updateEliStatus = async (id, statusId, statusNote = null) => {
    const completed = statusId === 'done';
    await supabase.from('eli_tasks').update({ status: statusId, completed, status_note: statusNote }).eq('id', id);
    setEliTasks(eliTasks.map(x => x.id === id ? { ...x, status: statusId, completed, status_note: statusNote } : x));
    setOpenStatusDropdown(null);
  };

  const handleEliStatusClick = (taskId, taskName, statusId) => {
    setOpenStatusDropdown(null);
    if (statusId === 'needs_context' || statusId === 'done' || statusId === 'invalid') {
      setContextModal({ id: taskId, taskName, statusId });
      setContextComment('');
    } else {
      updateEliStatus(taskId, statusId);
    }
  };

  const submitContextComment = async () => {
    if (!contextModal) return;
    await updateEliStatus(contextModal.id, contextModal.statusId, contextComment.trim() || null);
    setContextModal(null);
    setContextComment('');
  };

  const updateTannerStatus = async (id, statusId) => {
    const completed = statusId === 'done';
    await supabase.from('tanner_tasks').update({ status: statusId, completed }).eq('id', id);
    setTannerTasks(tannerTasks.map(x => x.id === id ? { ...x, status: statusId, completed } : x));
    setOpenStatusDropdown(null);
  };

  const addTannerTask = async () => {
    if (!newTaskName.trim()) return;
    setAddingTask(true);
    const { data, error } = await supabase.from('tanner_tasks').insert({
      task_name: newTaskName.trim(),
      due_date: newTaskDue || null,
      notes: newTaskNotes.trim() || null,
      priority: newTaskPriority,
      status: 'todo',
      completed: false,
    }).select().single();
    if (!error && data) {
      setTannerTasks([data, ...tannerTasks]);
    }
    setNewTaskName(''); setNewTaskDue(''); setNewTaskNotes(''); setNewTaskPriority('normal');
    setShowAddTask(false); setAddingTask(false);
  };

  const openEditGoal = (cat, g) => { setEditItem({ type: 'goal', cat, item: g }); setEditText(g.text); setEditDesc(g.description || ''); setEditDaily(g.isDaily || false); setEditRecurring(g.isRecurring || false); setEditDue(g.dueDate || ''); };

  const saveEdit = () => {
    if (!editItem) return;
    if (editItem.type === 'goal') {
      const g = { ...goals, [editItem.cat]: goals[editItem.cat].map(x => x.id === editItem.item.id ? { ...x, text: editText, description: editDesc.trim() || null, isDaily: editDaily, isRecurring: editRecurring, dueDate: editDue || null } : x) };
      setGoals(g); save(g);
    }
    setEditItem(null);
  };

  const deleteEdit = () => {
    if (!editItem) return;
    if (editItem.type === 'goal') removeGoal(editItem.cat, editItem.item.id);
    setEditItem(null);
  };

  const openEditTannerTask = (task) => {
    setEditTannerTask(task);
    setEditTannerName(task.task_name);
    setEditTannerDue(task.due_date || '');
    setEditTannerNotes(task.notes || '');
    setEditTannerPriority(task.priority || 'normal');
    setEditTannerStatus(task.status || 'todo');
  };

  const saveEditTannerTask = async () => {
    if (!editTannerTask || !editTannerName.trim()) return;
    setEditTannerSaving(true);
    const completed = editTannerStatus === 'done';
    const { error } = await supabase.from('tanner_tasks').update({
      task_name: editTannerName.trim(),
      due_date: editTannerDue || null,
      notes: editTannerNotes.trim() || null,
      priority: editTannerPriority,
      status: editTannerStatus,
      completed,
    }).eq('id', editTannerTask.id);
    if (!error) {
      setTannerTasks(tannerTasks.map(t => t.id === editTannerTask.id ? { ...t, task_name: editTannerName.trim(), due_date: editTannerDue || null, notes: editTannerNotes.trim() || null, priority: editTannerPriority, status: editTannerStatus, completed } : t));
      setEditTannerTask(null);
    }
    setEditTannerSaving(false);
  };

  const copy = (text, key) => { navigator.clipboard.writeText(text); setCopiedLink(key); setTimeout(() => setCopiedLink(null), 2000); };
  const renderLinks = (t) => t?.split(/(https?:\/\/[^\s]+)/g).map((p, i) => p.match(/https?:\/\//) ? <a key={i} href={p} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6' }} onClick={e => e.stopPropagation()}>{p}</a> : p);
  const fileIcon = (t) => t?.startsWith('image/') ? '🖼️' : t?.includes('pdf') ? '📄' : '📎';

  const highPriorityEli = eliTasks.filter(t => t.priority === 'high' && !t.completed && (t.status || 'todo') !== 'done');

  const isTaskHidden = (t) => hideDone && (t.status === 'done' || t.status === 'invalid' || t.completed);

  const getPriorityBorderColor = (priority) => {
    if (priority === 'high') return '#ef4444';
    if (priority === 'medium') return '#f59e0b';
    return 'transparent';
  };

  const cardStyle = { background: 'white', borderRadius: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.07), 0 4px 16px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#f1f5f9' }}>
      <div style={{ width: 40, height: 40, border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  const tannerDisplayTasks = sortTasks(tannerTasks.filter(t => !isTaskHidden(t)), tannerSort);
  const eliDisplayTasks = sortTasks(eliTasks.filter(t => !isTaskHidden(t)), eliSort);
  const tannerDoneCount = tannerTasks.filter(t => t.status === 'done' || t.completed).length;
  const eliDoneCount = eliTasks.filter(t => t.status === 'done' || t.completed).length;

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', color: '#0f172a', paddingBottom: 60, fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        input[type="date"] { color-scheme: light; }
        input, button, textarea, select { font-family: inherit; }
        .task-row { position: relative; }
        .task-row .action-btn { opacity: 0; transition: opacity 0.15s; }
        .task-row:hover .action-btn { opacity: 1; }
        .sort-select { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 4px 8px; font-size: 12px; color: #64748b; cursor: pointer; outline: none; }
        .sort-select:hover { border-color: #cbd5e1; }
        .status-badge { display: inline-flex; align-items: center; gap: 4px; padding: 3px 8px; border-radius: 6px; font-size: 11px; font-weight: 600; cursor: pointer; border: none; white-space: nowrap; }
        .status-badge:hover { filter: brightness(0.95); }
        .week-btn { padding: 7px 16px; border-radius: 20px; font-size: 13px; font-weight: 600; cursor: pointer; border: 2px solid #e2e8f0; background: white; color: #64748b; transition: all 0.15s; }
        .week-btn:hover { border-color: #3b82f6; color: #3b82f6; }
        .week-btn.active { background: #3b82f6; border-color: #3b82f6; color: white; }
        .add-task-input { width: 100%; border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px 14px; font-size: 14px; color: #0f172a; background: #f8fafc; outline: none; }
        .add-task-input:focus { border-color: #3b82f6; background: white; }
        .section-toggle { display: flex; align-items: center; gap: 10px; cursor: pointer; user-select: none; padding: 12px 0; }
        .section-toggle:hover { opacity: 0.8; }
      `}</style>

      {/* Status dropdown overlay */}
      {openStatusDropdown && (
        <div onClick={() => setOpenStatusDropdown(null)} style={{ position: 'fixed', inset: 0, zIndex: 50 }} />
      )}

      {/* Status comment modal (Done + Needs Context) */}
      {contextModal && (() => {
        const s = contextModal.statusId;
        const cfg = {
          done:          { icon: '✅', title: 'Mark as Done',    color: '#10b981', placeholder: 'Add a note to Eli (optional)...',       btn: 'Mark Done' },
          needs_context: { icon: '💬', title: 'Needs Context',   color: '#8b5cf6', placeholder: 'What context do you need from Eli?',    btn: 'Send to Slack' },
          invalid:       { icon: '🚫', title: 'Mark as Invalid', color: '#94a3b8', placeholder: 'Reason this is invalid (optional)...',   btn: 'Mark Invalid' },
        }[s] || {};
        return (
          <div onClick={() => setContextModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 16, padding: 28, maxWidth: 460, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <span style={{ fontSize: 20 }}>{cfg.icon}</span>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>{cfg.title}</h2>
              </div>
              <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}><strong style={{ color: cfg.color }}>{contextModal.taskName}</strong></p>
              <textarea
                autoFocus
                value={contextComment}
                onChange={e => setContextComment(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submitContextComment(); if (e.key === 'Escape') setContextModal(null); }}
                placeholder={cfg.placeholder}
                rows={4}
                style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', fontSize: 14, outline: 'none', resize: 'none', fontFamily: 'inherit', color: '#0f172a', marginBottom: 16 }}
              />
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button onClick={() => setContextModal(null)} style={{ padding: '10px 20px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: 'white', color: '#64748b', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
                <button onClick={submitContextComment} style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: cfg.color, color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>{cfg.btn}</button>
              </div>
              <p style={{ margin: '12px 0 0', fontSize: 11, color: '#94a3b8', textAlign: 'right' }}>⌘ + Enter to confirm</p>
            </div>
          </div>
        );
      })()}

      {/* Edit Goal Modal */}
      {editItem && (
        <div onClick={() => setEditItem(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 16, padding: 28, maxWidth: 500, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Edit Goal</h2>
              <button onClick={() => setEditItem(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 24, lineHeight: 1 }}>×</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <input value={editText} onChange={e => setEditText(e.target.value)} placeholder="Goal title" style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 10, padding: '11px 14px', fontSize: 14, color: '#0f172a', outline: 'none', background: '#f8fafc' }} />
              <textarea value={editDesc} onChange={e => setEditDesc(e.target.value)} placeholder="Description (optional)" rows={2} style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 10, padding: '11px 14px', fontSize: 14, color: '#0f172a', outline: 'none', resize: 'vertical', background: '#f8fafc' }} />
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', fontSize: 13 }}><input type="checkbox" checked={editDaily} onChange={e => setEditDaily(e.target.checked)} /> Track daily</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', fontSize: 13 }}><input type="checkbox" checked={editRecurring} onChange={e => setEditRecurring(e.target.checked)} /> Weekly recurring</label>
              <input type="date" value={editDue} onChange={e => setEditDue(e.target.value)} style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 10, padding: '11px 14px', fontSize: 14, color: '#0f172a', outline: 'none', background: '#f8fafc' }} />
              <div style={{ display: 'flex', gap: 8, paddingTop: 4 }}>
                <button onClick={saveEdit} style={{ flex: 1, padding: '11px 20px', borderRadius: 10, border: 'none', background: '#3b82f6', color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>Save</button>
                <button onClick={() => setEditItem(null)} style={{ padding: '11px 20px', borderRadius: 10, border: '1px solid #e2e8f0', background: 'white', color: '#64748b', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
                <button onClick={deleteEdit} style={{ padding: '11px 20px', borderRadius: 10, border: 'none', background: '#fef2f2', color: '#ef4444', fontWeight: 700, cursor: 'pointer', fontSize: 14, marginLeft: 'auto' }}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Tanner Task Modal */}
      {editTannerTask && (
        <div onClick={() => setEditTannerTask(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 16, padding: 28, maxWidth: 500, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Edit Task</h2>
              <button onClick={() => setEditTannerTask(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 24, lineHeight: 1 }}>×</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Task Name</label>
                <input value={editTannerName} onChange={e => setEditTannerName(e.target.value)} style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 10, padding: '11px 14px', fontSize: 14, color: '#0f172a', outline: 'none', background: '#f8fafc' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Priority</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {PRIORITIES.map(p => (
                    <button key={p.id} type="button" onClick={() => setEditTannerPriority(p.id)} style={{ flex: 1, padding: '9px 10px', border: editTannerPriority === p.id ? `2px solid ${p.color}` : '2px solid #e2e8f0', background: editTannerPriority === p.id ? `${p.color}15` : 'white', color: editTannerPriority === p.id ? p.color : '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer', borderRadius: 8 }}>{p.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</label>
                <select value={editTannerStatus} onChange={e => setEditTannerStatus(e.target.value)} style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 10, padding: '11px 14px', fontSize: 14, color: '#0f172a', outline: 'none', background: '#f8fafc' }}>
                  {TANNER_STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Due Date</label>
                <input type="date" value={editTannerDue} onChange={e => setEditTannerDue(e.target.value)} style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 10, padding: '11px 14px', fontSize: 14, color: '#0f172a', outline: 'none', background: '#f8fafc' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notes</label>
                <textarea value={editTannerNotes} onChange={e => setEditTannerNotes(e.target.value)} rows={3} style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 10, padding: '11px 14px', fontSize: 14, color: '#0f172a', outline: 'none', resize: 'vertical', background: '#f8fafc' }} />
              </div>
              <div style={{ display: 'flex', gap: 8, paddingTop: 4 }}>
                <button onClick={saveEditTannerTask} disabled={editTannerSaving} style={{ flex: 1, padding: '11px 20px', borderRadius: 10, border: 'none', background: '#3b82f6', color: 'white', fontWeight: 700, cursor: editTannerSaving ? 'not-allowed' : 'pointer', fontSize: 14, opacity: editTannerSaving ? 0.7 : 1 }}>{editTannerSaving ? 'Saving...' : 'Save Changes'}</button>
                <button onClick={() => setEditTannerTask(null)} style={{ padding: '11px 20px', borderRadius: 10, border: '1px solid #e2e8f0', background: 'white', color: '#64748b', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navbar */}
      <div style={{ background: 'white', borderBottom: '1px solid #e2e8f0', padding: '0 24px', position: 'sticky', top: 0, zIndex: 40 }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <span style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>Taskpitch</span>
            <div style={{ display: 'flex', gap: 6 }}>
              {[-2, -1, 0, 1].map(o => (
                <button key={o} className={`week-btn${selectedWeek === o ? ' active' : ''}`} onClick={() => setSelectedWeek(o)}>
                  {o === 0 ? 'This Week' : o === -1 ? 'Last Week' : o === 1 ? 'Next Week' : `${Math.abs(o)}w Ago`}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {saving && <span style={{ fontSize: 12, color: '#94a3b8' }}>Saving...</span>}
            <span style={{ fontSize: 13, color: '#94a3b8' }}>{formatWeek(weekStart)}</span>
            <button onClick={() => supabase.auth.signOut()} style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', color: '#64748b', fontSize: 13, fontWeight: 600 }}>Sign Out</button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '24px 20px' }}>

        {/* High Priority Alert */}
        {highPriorityEli.length > 0 && (
          <div style={{ background: '#fef2f2', border: '2px solid #ef4444', borderRadius: 14, padding: '14px 18px', marginBottom: 24, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <span style={{ fontSize: 20, flexShrink: 0 }}>🔴</span>
            <div>
              <div style={{ fontWeight: 700, color: '#dc2626', fontSize: 15, marginBottom: 6 }}>High Priority from Eli ({highPriorityEli.length})</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {highPriorityEli.map(t => (
                  <div key={t.id} style={{ fontSize: 13, color: '#7f1d1d', fontWeight: 500 }}>• {t.task_name}{t.due_date && ` — due ${formatDue(t.due_date)?.label}`}</div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Tasks Section */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>Tasks</h2>
          <button onClick={() => setHideDone(h => !h)} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, color: '#64748b', cursor: 'pointer' }}>
            {hideDone ? `Show Done (${tannerDoneCount + eliDoneCount})` : 'Hide Done'}
          </button>
        </div>

        {/* Two-column task grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 32 }}>

          {/* MY TASKS column */}
          <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>⚡</div>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>My Tasks</div>
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>{tannerTasks.filter(t => !isTaskHidden(t)).length} open</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <select className="sort-select" value={tannerSort} onChange={e => setTannerSort(e.target.value)}>
                    <option value="priority">Priority</option>
                    <option value="due">Due Date</option>
                    <option value="status">Status</option>
                  </select>
                  <button onClick={loadTanner} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', fontSize: 14, color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>↻</button>
                </div>
              </div>
            </div>

            <div style={{ flex: 1, padding: '12px 20px', overflowY: 'auto', maxHeight: 520 }}>
              {tannerDisplayTasks.length === 0 && !showAddTask && (
                <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
                  {hideDone && tannerTasks.length > 0 ? 'All done! 🎉' : 'No tasks yet'}
                </p>
              )}
              {tannerDisplayTasks.map(t => {
                const due = formatDue(t.due_date);
                const status = getStatus(t.status || 'todo', TANNER_STATUSES);
                const priority = PRIORITIES.find(p => p.id === (t.priority || 'normal'));
                const isExpanded = expandedTasks[t.id];
                const dropKey = `tanner-${t.id}`;
                return (
                  <div key={t.id} className="task-row" style={{ borderLeft: `3px solid ${getPriorityBorderColor(t.priority || 'normal')}`, borderRadius: '0 8px 8px 0', marginBottom: 6, background: '#fafafa', padding: '10px 12px', cursor: 'pointer' }} onClick={() => setExpandedTasks(e => ({ ...e, [t.id]: !e[t.id] }))}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {/* Status badge dropdown */}
                      <div style={{ position: 'relative', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                        <button className="status-badge" onClick={() => setOpenStatusDropdown(openStatusDropdown === dropKey ? null : dropKey)} style={{ background: status.bg, color: status.color }}>
                          {status.label} ▾
                        </button>
                        {openStatusDropdown === dropKey && (
                          <div style={{ position: 'absolute', top: '110%', left: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.1)', zIndex: 100, minWidth: 140, overflow: 'hidden' }}>
                            {TANNER_STATUSES.map(s => (
                              <button key={s.id} onClick={() => updateTannerStatus(t.id, s.id)} style={{ display: 'block', width: '100%', padding: '10px 14px', border: 'none', background: t.status === s.id ? s.bg : 'white', color: s.color, fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>
                                {s.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: '#0f172a', textDecoration: (t.status === 'done' || t.completed) ? 'line-through' : 'none', opacity: (t.status === 'done' || t.completed) ? 0.5 : 1 }}>{t.task_name}</span>
                      {(t.priority && t.priority !== 'normal') && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 5, background: t.priority === 'high' ? '#fef2f2' : '#fffbeb', color: priority?.color }}>{priority?.label}</span>
                      )}
                      {due && (
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 5, background: `${due.color}18`, color: due.color, whiteSpace: 'nowrap' }}>{due.label}</span>
                      )}
                      <button className="action-btn" onClick={e => { e.stopPropagation(); openEditTannerTask(t); }} style={{ background: '#f1f5f9', border: 'none', borderRadius: 6, width: 26, height: 26, cursor: 'pointer', fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>✏️</button>
                      <button className="action-btn" onClick={e => { e.stopPropagation(); deleteTanner(t.id); }} style={{ background: '#fef2f2', border: 'none', borderRadius: 6, width: 26, height: 26, cursor: 'pointer', fontSize: 14, color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>×</button>
                    </div>
                    {isExpanded && t.notes && (
                      <div style={{ marginTop: 8, paddingLeft: 4, fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>{renderLinks(t.notes)}</div>
                    )}
                  </div>
                );
              })}

              {/* Inline add task form */}
              {showAddTask ? (
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginTop: 8 }}>
                  <input className="add-task-input" value={newTaskName} onChange={e => setNewTaskName(e.target.value)} placeholder="Task name..." autoFocus onKeyDown={e => e.key === 'Enter' && addTannerTask()} />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <input type="date" className="add-task-input" value={newTaskDue} onChange={e => setNewTaskDue(e.target.value)} style={{ flex: 1 }} />
                    <select className="add-task-input" value={newTaskPriority} onChange={e => setNewTaskPriority(e.target.value)} style={{ flex: 1 }}>
                      <option value="normal">Normal</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  <textarea className="add-task-input" value={newTaskNotes} onChange={e => setNewTaskNotes(e.target.value)} placeholder="Notes (optional)..." rows={2} style={{ marginTop: 8, resize: 'vertical' }} />
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button onClick={addTannerTask} disabled={addingTask} style={{ padding: '9px 18px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: addingTask ? 'not-allowed' : 'pointer', opacity: addingTask ? 0.7 : 1 }}>{addingTask ? 'Adding...' : 'Add Task'}</button>
                    <button onClick={() => { setShowAddTask(false); setNewTaskName(''); setNewTaskDue(''); setNewTaskNotes(''); setNewTaskPriority('normal'); }} style={{ padding: '9px 18px', background: 'white', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setShowAddTask(true)} style={{ width: '100%', padding: '10px 14px', marginTop: 6, background: 'transparent', border: '1px dashed #cbd5e1', borderRadius: 8, color: '#94a3b8', fontSize: 13, fontWeight: 500, cursor: 'pointer', textAlign: 'left' }}>
                  + Add task...
                </button>
              )}
            </div>

            {/* Column footer */}
            <div style={{ padding: '10px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 6 }}>
              <code style={{ fontSize: 10, color: '#94a3b8', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{window.location.origin}/#/add</code>
              <button onClick={() => copy(`${window.location.origin}/#/add`, 'add')} style={{ background: copiedLink === 'add' ? '#f0fdf4' : '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 11, color: copiedLink === 'add' ? '#10b981' : '#94a3b8', fontWeight: 600 }}>{copiedLink === 'add' ? '✓ Copied' : 'Copy'}</button>
            </div>
          </div>

          {/* FROM ELI column */}
          <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>📬</div>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>From Eli</div>
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>{eliTasks.filter(t => !isTaskHidden(t)).length} open</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <select className="sort-select" value={eliSort} onChange={e => setEliSort(e.target.value)}>
                    <option value="priority">Priority</option>
                    <option value="due">Due Date</option>
                    <option value="status">Status</option>
                  </select>
                  <button onClick={loadEli} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', fontSize: 14, color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>↻</button>
                </div>
              </div>
            </div>

            <div style={{ flex: 1, padding: '12px 20px', overflowY: 'auto', maxHeight: 570 }}>
              {eliDisplayTasks.length === 0 && (
                <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
                  {hideDone && eliTasks.length > 0 ? 'All done! 🎉' : 'No tasks yet'}
                </p>
              )}
              {eliDisplayTasks.map(t => {
                const due = formatDue(t.due_date);
                const status = getStatus(t.status || 'todo', ELI_STATUSES);
                const priority = PRIORITIES.find(p => p.id === (t.priority || 'normal'));
                const isExpanded = expandedTasks[t.id];
                const att = t.attachments || [];
                const dropKey = `eli-${t.id}`;
                return (
                  <div key={t.id} className="task-row" style={{ borderLeft: `3px solid ${getPriorityBorderColor(t.priority || 'normal')}`, borderRadius: '0 8px 8px 0', marginBottom: 6, background: '#fafafa', padding: '10px 12px', cursor: 'pointer' }} onClick={() => setExpandedTasks(e => ({ ...e, [t.id]: !e[t.id] }))}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {/* Status badge dropdown */}
                      <div style={{ position: 'relative', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                        <button className="status-badge" onClick={() => setOpenStatusDropdown(openStatusDropdown === dropKey ? null : dropKey)} style={{ background: status.bg, color: status.color }}>
                          {status.label} ▾
                        </button>
                        {openStatusDropdown === dropKey && (
                          <div style={{ position: 'absolute', top: '110%', left: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.1)', zIndex: 100, minWidth: 150, overflow: 'hidden' }}>
                            {ELI_STATUSES.map(s => (
                              <button key={s.id} onClick={() => handleEliStatusClick(t.id, t.task_name, s.id)} style={{ display: 'block', width: '100%', padding: '10px 14px', border: 'none', background: (t.status || 'todo') === s.id ? s.bg : 'white', color: s.color, fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>
                                {s.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: '#0f172a', textDecoration: (t.status === 'done' || t.completed) ? 'line-through' : 'none', opacity: (t.status === 'done' || t.completed) ? 0.5 : 1 }}>{t.task_name}</span>
                      {(t.priority && t.priority !== 'normal') && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 5, background: t.priority === 'high' ? '#fef2f2' : '#fffbeb', color: priority?.color }}>{priority?.label}</span>
                      )}
                      {due && (
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 5, background: `${due.color}18`, color: due.color, whiteSpace: 'nowrap' }}>{due.label}</span>
                      )}
                      {att.length > 0 && (
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 5, background: '#eff6ff', color: '#3b82f6' }}>📎 {att.length}</span>
                      )}
                      <button className="action-btn" onClick={e => { e.stopPropagation(); deleteEli(t.id); }} style={{ background: '#fef2f2', border: 'none', borderRadius: 6, width: 26, height: 26, cursor: 'pointer', fontSize: 14, color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>×</button>
                    </div>
                    {isExpanded && (
                      <div style={{ marginTop: 8, paddingLeft: 4 }}>
                        {t.notes && <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5, marginBottom: att.length > 0 ? 8 : 0 }}>{renderLinks(t.notes)}</div>}
                        {att.length > 0 && <div>{att.map((f, i) => (
                          <a key={i} href={f.url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#eff6ff', padding: '4px 8px', borderRadius: 6, textDecoration: 'none', fontSize: 11, marginRight: 6, marginBottom: 4 }}>
                            <span>{fileIcon(f.type)}</span><span style={{ color: '#3b82f6' }}>{f.name}</span>
                          </a>
                        ))}</div>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Column footer */}
            <div style={{ padding: '10px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 6 }}>
              <code style={{ fontSize: 10, color: '#94a3b8', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{window.location.origin}/#/eli</code>
              <button onClick={() => copy(`${window.location.origin}/#/eli`, 'eli')} style={{ background: copiedLink === 'eli' ? '#f0fdf4' : '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 11, color: copiedLink === 'eli' ? '#10b981' : '#94a3b8', fontWeight: 600 }}>{copiedLink === 'eli' ? '✓ Copied' : 'Copy'}</button>
            </div>
          </div>
        </div>

        {/* Goals Section (collapsed by default) */}
        <div style={{ ...cardStyle, marginBottom: 20 }}>
          <div className="section-toggle" style={{ padding: '16px 20px', borderBottom: goalsCollapsed ? 'none' : '1px solid #f1f5f9' }} onClick={() => setGoalsCollapsed(c => !c)}>
            <span style={{ fontSize: 18, fontWeight: 800, color: '#94a3b8', letterSpacing: '-0.3px' }}>🎯 Goals</span>
            <span style={{ marginLeft: 'auto', fontSize: 18, color: '#94a3b8', transform: goalsCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▾</span>
          </div>
          {!goalsCollapsed && (
            <div style={{ padding: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
                {Object.entries(cats).map(([cat, cfg]) => {
                  const items = goals[cat] || [];
                  const isBiz = ['premium', 'dripjobs', 'routemize'].includes(cat);
                  return (
                    <div key={cat} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 14, padding: 18 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                        <div style={{ width: 30, height: 30, borderRadius: 8, background: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: cfg.color, fontWeight: 700, fontSize: 14 }}>{cfg.icon}</div>
                        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: cfg.color }}>{cfg.label}</h3>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {items.map(g => {
                          const due = formatDue(g.dueDate);
                          return (
                            <div key={g.id} style={{ padding: '9px 11px', background: g.completed ? '#f0fdf4' : 'white', borderRadius: 9, border: '1px solid #e2e8f0' }}>
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
                                <div onClick={() => toggleGoal(cat, g.id)} style={{ width: 20, height: 20, borderRadius: 5, border: g.completed ? 'none' : '2px solid #cbd5e1', background: g.completed ? '#10b981' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, color: 'white', fontSize: 11, marginTop: 1 }}>{g.completed && '✓'}</div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: 13, color: '#0f172a', textDecoration: g.completed ? 'line-through' : 'none', opacity: g.completed ? 0.5 : 1 }}>{g.text}</span>
                                    {g.isRecurring && <span style={{ fontSize: 9, padding: '2px 5px', borderRadius: 5, background: '#eff6ff', color: '#3b82f6', fontWeight: 700 }}>↻</span>}
                                    {due && <span style={{ fontSize: 10, padding: '2px 5px', borderRadius: 5, fontWeight: 600, background: `${due.color}18`, color: due.color }}>{due.label}</span>}
                                  </div>
                                  {g.description && <p style={{ margin: '3px 0 0', fontSize: 11, color: '#64748b', lineHeight: 1.4 }}>{g.description}</p>}
                                  {g.isDaily && (
                                    <div style={{ display: 'flex', gap: 3, marginTop: 7 }}>
                                      {DAYS.map(d => {
                                        const checked = dailyTracking[d]?.[`${cat}-${g.id}`];
                                        const isToday = d === currentDay;
                                        return (
                                          <div key={d} onClick={() => toggleDaily(cat, g.id, d)} title={d} style={{ width: 22, height: 22, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, cursor: 'pointer', background: checked ? cfg.color : '#f1f5f9', color: checked ? 'white' : '#94a3b8', border: isToday ? `2px solid ${cfg.color}` : '2px solid transparent' }}>{d[0]}</div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                                <button onClick={() => openEditGoal(cat, g)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, padding: '1px 5px', color: '#94a3b8' }}>⚙️</button>
                              </div>
                            </div>
                          );
                        })}
                        {editingGoal === cat ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12, background: 'white', borderRadius: 9, border: '1px solid #e2e8f0' }}>
                            <input placeholder="Goal title..." value={newGoalText} onChange={e => setNewGoalText(e.target.value)} autoFocus style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 8, padding: '9px 12px', fontSize: 13, color: '#0f172a', outline: 'none' }} />
                            <textarea placeholder="Description (optional)..." value={newGoalDesc} onChange={e => setNewGoalDesc(e.target.value)} rows={2} style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 8, padding: '9px 12px', fontSize: 13, color: '#0f172a', outline: 'none', resize: 'vertical' }} />
                            {!isBiz && <label style={{ display: 'flex', alignItems: 'center', gap: 7, color: '#64748b', fontSize: 12 }}><input type="checkbox" checked={newGoalDaily} onChange={e => setNewGoalDaily(e.target.checked)} /> Track daily</label>}
                            <label style={{ display: 'flex', alignItems: 'center', gap: 7, color: '#64748b', fontSize: 12 }}><input type="checkbox" checked={newGoalRecurring} onChange={e => setNewGoalRecurring(e.target.checked)} /> Weekly recurring</label>
                            <input type="date" value={newGoalDue} onChange={e => setNewGoalDue(e.target.value)} style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 8, padding: '9px 12px', fontSize: 13, color: '#0f172a', outline: 'none' }} />
                            <div style={{ display: 'flex', gap: 7 }}>
                              <button onClick={() => addGoal(cat)} style={{ padding: '8px 15px', borderRadius: 8, border: 'none', background: '#3b82f6', color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>Add</button>
                              <button onClick={() => { setEditingGoal(null); setNewGoalText(''); setNewGoalDesc(''); }} style={{ padding: '8px 15px', borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', color: '#64748b', fontWeight: 600, cursor: 'pointer', fontSize: 12 }}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <button onClick={() => setEditingGoal(cat)} style={{ background: 'transparent', border: '1px dashed #cbd5e1', borderRadius: 9, padding: '8px 12px', color: '#94a3b8', cursor: 'pointer', fontSize: 12, width: '100%', textAlign: 'left' }}>+ Add Goal</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Workouts Section (collapsed by default) */}
        <div style={cardStyle}>
          <div className="section-toggle" style={{ padding: '16px 20px', borderBottom: workoutsCollapsed ? 'none' : '1px solid #f1f5f9' }} onClick={() => setWorkoutsCollapsed(c => !c)}>
            <span style={{ fontSize: 18, fontWeight: 800, color: '#94a3b8', letterSpacing: '-0.3px' }}>🏃 Workouts</span>
            <span style={{ marginLeft: 'auto', fontSize: 18, color: '#94a3b8', transform: workoutsCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▾</span>
          </div>
          {!workoutsCollapsed && (
            <div style={{ padding: '20px', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 580 }}>
                <thead>
                  <tr>{['Day', 'Run', 'Miles', 'Time', 'Gym', 'Gym Time', 'Weight'].map(h => (
                    <th key={h} style={{ textAlign: h === 'Day' ? 'left' : 'center', padding: '8px 12px', color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {DAYS.map(d => {
                    const w = workouts[d] || {};
                    const isToday = d === currentDay;
                    return (
                      <tr key={d} style={{ background: isToday ? '#eff6ff' : 'transparent', borderTop: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '9px 12px', fontWeight: isToday ? 700 : 500, color: isToday ? '#3b82f6' : '#374151', fontSize: 13 }}>{d.slice(0, 3)}{isToday && ' •'}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'center' }}>
                          <div onClick={() => updateWorkout(d, 'run', !w.run)} style={{ width: 24, height: 24, borderRadius: 6, border: `2px solid ${w.run ? '#10b981' : '#e2e8f0'}`, background: w.run ? '#10b981' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', margin: '0 auto', color: 'white', fontSize: 13 }}>{w.run && '✓'}</div>
                        </td>
                        <td style={{ padding: '9px 12px', textAlign: 'center' }}><input type="number" step="0.1" value={w.miles || ''} onChange={e => updateWorkout(d, 'miles', e.target.value)} placeholder="0" style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '5px 8px', color: '#0f172a', width: 66, fontSize: 12, textAlign: 'center', background: 'white', outline: 'none' }} /></td>
                        <td style={{ padding: '9px 12px', textAlign: 'center' }}><input value={w.time || ''} onChange={e => updateWorkout(d, 'time', e.target.value)} placeholder="0:00" style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '5px 8px', color: '#0f172a', width: 66, fontSize: 12, textAlign: 'center', background: 'white', outline: 'none' }} /></td>
                        <td style={{ padding: '9px 12px', textAlign: 'center' }}>
                          <div onClick={() => updateWorkout(d, 'gym', !w.gym)} style={{ width: 24, height: 24, borderRadius: 6, border: `2px solid ${w.gym ? '#8b5cf6' : '#e2e8f0'}`, background: w.gym ? '#8b5cf6' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', margin: '0 auto', color: 'white', fontSize: 13 }}>{w.gym && '✓'}</div>
                        </td>
                        <td style={{ padding: '9px 12px', textAlign: 'center' }}><input value={w.gymTime || ''} onChange={e => updateWorkout(d, 'gymTime', e.target.value)} placeholder="0:00" style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '5px 8px', color: '#0f172a', width: 66, fontSize: 12, textAlign: 'center', background: 'white', outline: 'none' }} /></td>
                        <td style={{ padding: '9px 12px', textAlign: 'center' }}><input type="number" step="0.1" value={w.weight || ''} onChange={e => updateWorkout(d, 'weight', e.target.value)} placeholder="lbs" style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '5px 8px', color: '#0f172a', width: 66, fontSize: 12, textAlign: 'center', background: 'white', outline: 'none' }} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

export default WeeklyTracker;
